import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { tokenStore } from "../lib/api";
import { authService } from "../services/authService";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();

  const fetchMe = useCallback(async () => {
    try {
      const data = await authService.me();
      setUser(data);
    } catch (e) {
      console.warn("auth me failed (likely unauthenticated)", e);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tokenStore.get()) {
      fetchMe();
    } else {
      setUser(false);
      setLoading(false);
    }
  }, [fetchMe]);

  // iter-169zzp — PWA installed-app session resilience.
  // When the user reopens the installed Lumfi app after backgrounding it
  // for hours/days, the access token may have expired. We call /auth/me
  // on visibilitychange → if it 401s, the response interceptor in
  // api.js will issue a /auth/refresh using the (90-day) refresh cookie
  // and the next request succeeds. Without this, the user briefly sees
  // a logged-out state when reopening — feels broken.
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    const isPwa =
      (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) ||
      Boolean(window.navigator && window.navigator.standalone);
    if (!isPwa) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible" && tokenStore.get()) {
        fetchMe();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchMe]);

  const login = async (email, password, extra = {}) => {
    const data = await authService.login(email, password, extra);
    if (data.access_token) tokenStore.set(data.access_token);
    setUser(data);
    return data;
  };

  const register = async (email, password, extra = {}) => {
    const data = await authService.register(email, password, extra);
    // iter-169l — register no longer signs the user in. The response
    // ships `pending_verification: true` and the page navigates to the
    // /verify-email-pending screen.
    if (data.access_token) tokenStore.set(data.access_token);
    if (!data.pending_verification) setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (e) {
      // network failure is fine — we still drop the local token
      console.warn("logout request failed", e);
    }
    tokenStore.clear();
    setUser(false);
    // Drop ALL react-query caches so the next user's session doesn't leak
    // stale data (capabilities, packs, dashboard, etc.).
    qc.clear();
  };

  return (
    <AuthCtx.Provider
      value={{ user, setUser, loading, login, register, logout, refresh: fetchMe }}
    >
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
